import React, { useContext, useRef, useState } from "react";
import excelToJson from "../utils/excelToJson";
import { geocodeRecords, GeocodingStats } from "../utils/geocodeRecords";
import {
  AlertCircle, CheckCircle2, CloudUpload, FileSpreadsheet,
  MapPin, Navigation, RotateCcw, X,
} from "lucide-react";
import axiosInstance from "../utils/axios";
import { AuthContext } from "../contexts/Auth/Auth.Context";

/* ── Phase ──────────────────────────────────────────────────────────── */
type Phase = "idle" | "parsing" | "geocoding" | "ready" | "uploading" | "done";

/* ── Tiny progress bar ───────────────────────────────────────────────── */
function Bar({ value, color = "#22c55e", animate = false }: { value: number; color?: string; animate?: boolean }) {
  return (
    <div className="w-full h-1.5 rounded-full overflow-hidden" style={{ background: "rgba(255,255,255,0.07)" }}>
      <div
        className={animate ? "animate-pulse" : ""}
        style={{
          width: `${value}%`,
          height: "100%",
          background: color,
          borderRadius: 9999,
          transition: "width 0.3s ease",
        }}
      />
    </div>
  );
}

/* ── Main component ──────────────────────────────────────────────────── */
const ExcelUploader: React.FC = () => {
  const { state } = useContext(AuthContext);
  const fileRef = useRef<HTMLInputElement>(null);

  const [phase,         setPhase]         = useState<Phase>("idle");
  const [data,          setData]          = useState<any[]>([]);
  const [fileName,      setFileName]      = useState<string | null>(null);
  const [error,         setError]         = useState<string | null>(null);
  const [uploadPercent, setUploadPercent] = useState(0);
  const [gStats,        setGStats]        = useState<GeocodingStats | null>(null);
  const [dragging,      setDragging]      = useState(false);

  /* ── Derived ──────────────────────────────────────────────────────── */
  const isParsing   = phase === "parsing";
  const isGeocoding = phase === "geocoding";
  const isUploading = phase === "uploading";
  const isReady     = phase === "ready";
  const isDone      = phase === "done";
  const isBusy      = isParsing || isGeocoding || isUploading;

  const geocodePercent = gStats
    ? Math.round((gStats.uniqueDone / Math.max(gStats.uniqueTotal, 1)) * 100)
    : 0;

  /* ── Process file ─────────────────────────────────────────────────── */
  async function processFile(file: File) {
    setError(null);
    setData([]);
    setGStats(null);
    setFileName(file.name);

    setPhase("parsing");
    let parsed: any[];
    try {
      parsed = await excelToJson(file);
    } catch {
      setError("Failed to parse the Excel file. Please check the format.");
      setPhase("idle");
      return;
    }
    setPhase("geocoding");
    let geocoded: any[];
    try {
      geocoded = await geocodeRecords(parsed, (s) => setGStats({ ...s }));
    } catch {
      setError("Geocoding failed — coordinates will be missing for some records.");
      geocoded = parsed;
    }
    setData(geocoded);
    setPhase("ready");
  }

  function handleInput(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (file) processFile(file);
  }

  function handleDrop(e: React.DragEvent) {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file && (file.name.endsWith(".xlsx") || file.name.endsWith(".xls"))) processFile(file);
  }

  /* ── Upload ───────────────────────────────────────────────────────── */
  function submitData() {
    setPhase("uploading");
    setUploadPercent(0);
    axiosInstance
      .post("/api/v1/seizure-cases/upload", data, {
        headers: { Authorization: `Bearer ${state.userToken}` },
        onUploadProgress: (evt) => {
          if (evt.total) setUploadPercent(Math.min(99, Math.round((evt.loaded / evt.total) * 100)));
        },
      })
      .then((res) => {
        if (res.status === 200) {
          setUploadPercent(100);
          setPhase("done");
          setTimeout(() => window.location.reload(), 2000);
        }
      })
      .catch((err) => {
        setError(err?.response?.data?.message ?? err?.message ?? "Upload failed. Please try again.");
        setPhase("ready");
        setUploadPercent(0);
      });
  }

  /* ── Reset ────────────────────────────────────────────────────────── */
  function reset() {
    setPhase("idle");
    setData([]);
    setError(null);
    setGStats(null);
    setFileName(null);
    setUploadPercent(0);
    if (fileRef.current) fileRef.current.value = "";
  }

  /* ── Render ───────────────────────────────────────────────────────── */
  return (
    <div
      className="flex flex-col gap-4 h-full"
      style={{ color: "rgba(255,255,255,0.75)", fontFamily: "var(--font-body, sans-serif)" }}
    >
      {/* ── Info strip ─────────────────────────────────────────────── */}
      <div
        className="flex items-start gap-3 px-4 py-3 rounded-xl text-xs"
        style={{ background: "rgba(34,197,94,0.07)", border: "1px solid rgba(34,197,94,0.18)" }}
      >
        <MapPin size={14} className="text-green-400 mt-0.5 shrink-0" />
        <p style={{ color: "rgba(255,255,255,0.55)", lineHeight: 1.6 }}>
          Coordinates are resolved automatically via <strong style={{ color: "rgba(255,255,255,0.75)" }}>Mapbox Geocoding</strong> before
          upload. Records that cannot be pinpointed precisely fall back to their country&apos;s centre-point.
        </p>
      </div>

      {/* ── Drop zone / file picker ─────────────────────────────────── */}
      {phase === "idle" && (
        <div
          role="button"
          tabIndex={0}
          onClick={() => fileRef.current?.click()}
          onKeyDown={(e) => e.key === "Enter" && fileRef.current?.click()}
          onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={handleDrop}
          className="flex flex-col items-center justify-center gap-3 py-12 rounded-2xl cursor-pointer transition-all duration-200"
          style={{
            border: `2px dashed ${dragging ? "rgba(34,197,94,0.6)" : "rgba(255,255,255,0.1)"}`,
            background: dragging ? "rgba(34,197,94,0.05)" : "rgba(255,255,255,0.02)",
          }}
        >
          <div
            className="w-14 h-14 rounded-2xl flex items-center justify-center"
            style={{ background: "rgba(34,197,94,0.1)", border: "1px solid rgba(34,197,94,0.2)" }}
          >
            <CloudUpload size={26} className="text-green-400" />
          </div>
          <div className="text-center space-y-1">
            <p className="text-sm font-semibold" style={{ color: "rgba(255,255,255,0.8)" }}>
              Drop your Excel file here
            </p>
            <p className="text-xs" style={{ color: "rgba(255,255,255,0.35)" }}>
              or click to browse &mdash; <span className="font-mono">.xlsx</span> / <span className="font-mono">.xls</span>
            </p>
          </div>
        </div>
      )}

      {/* hidden real input */}
      <input ref={fileRef} type="file" accept=".xlsx,.xls" onChange={handleInput} className="hidden" />

      {/* ── File chosen indicator ───────────────────────────────────── */}
      {fileName && phase !== "idle" && (
        <div
          className="flex items-center gap-3 px-4 py-3 rounded-xl"
          style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)" }}
        >
          <FileSpreadsheet size={16} className="text-green-400 shrink-0" />
          <span className="text-xs font-medium truncate flex-1" style={{ color: "rgba(255,255,255,0.65)" }}>
            {fileName}
          </span>
          {!isBusy && !isDone && (
            <button onClick={reset} className="text-white/25 hover:text-white/60 transition-colors">
              <X size={14} />
            </button>
          )}
        </div>
      )}

      {/* ── Parsing ─────────────────────────────────────────────────── */}
      {isParsing && (
        <StatusCard icon={<SpinnerIcon />} label="Reading file&hellip;" sub="Parsing spreadsheet rows" color="rgba(96,165,250,1)">
          <Bar value={50} color="rgba(96,165,250,0.8)" animate />
        </StatusCard>
      )}

      {/* ── Geocoding progress ──────────────────────────────────────── */}
      {isGeocoding && (
        <StatusCard icon={<SpinnerIcon />} label="Resolving coordinates&hellip;" sub={gStats ? `${gStats.uniqueDone} / ${gStats.uniqueTotal} unique locations` : "Starting…"} color="rgba(251,191,36,1)">
          <Bar value={geocodePercent} color="rgba(251,191,36,0.9)" animate />
        </StatusCard>
      )}

      {/* ── Geocoding summary ───────────────────────────────────────── */}
      {(isReady || isUploading || isDone) && gStats && (
        <div
          className="rounded-xl overflow-hidden"
          style={{ border: "1px solid rgba(255,255,255,0.08)", background: "rgba(255,255,255,0.02)" }}
        >
          <div
            className="px-4 py-2.5 flex items-center justify-between"
            style={{ borderBottom: "1px solid rgba(255,255,255,0.07)" }}
          >
            <span className="text-[10px] font-semibold tracking-[0.12em] uppercase" style={{ color: "rgba(255,255,255,0.3)" }}>
              Geocoding Summary
            </span>
            <span className="text-[10px] font-mono" style={{ color: "rgba(255,255,255,0.3)" }}>
              {data.length} records
            </span>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4" style={{ borderTop: "none" }}>
            <StatCell icon={<CheckCircle2 size={13} className="text-emerald-400" />} label="Precise"       value={gStats.precise}     sub="api_precise"      color="text-emerald-400" border />
            <StatCell icon={<Navigation  size={13} className="text-sky-400"     />} label="Approximate"   value={gStats.approximate} sub="api_approximate"  color="text-sky-400"     border />
            <StatCell icon={<MapPin      size={13} className="text-amber-400"   />} label="Country Centre" value={gStats.centroid}    sub="country_centroid" color="text-amber-400"   border />
            <StatCell icon={<CheckCircle2 size={13} className="text-white/30"   />} label="Pre-provided"  value={gStats.provided}    sub="from source"      color="text-white/40"    border={false} />
          </div>
        </div>
      )}

      {/* ── Upload progress ─────────────────────────────────────────── */}
      {isUploading && (
        <StatusCard
          icon={<SpinnerIcon color="rgba(34,197,94,1)" />}
          label={`Uploading ${data.length} records…`}
          sub={`${uploadPercent}% transferred`}
          color="rgba(34,197,94,1)"
        >
          <Bar value={uploadPercent} color="rgba(34,197,94,0.9)" />
        </StatusCard>
      )}

      {/* ── Success ─────────────────────────────────────────────────── */}
      {isDone && (
        <div
          className="flex items-start gap-3 px-4 py-3.5 rounded-xl"
          style={{ background: "rgba(34,197,94,0.1)", border: "1px solid rgba(34,197,94,0.25)" }}
        >
          <CheckCircle2 size={16} className="text-green-400 mt-0.5 shrink-0" />
          <div>
            <p className="text-sm font-semibold text-green-400">Upload successful</p>
            <p className="text-xs mt-0.5" style={{ color: "rgba(255,255,255,0.45)" }}>
              {data.length} records saved. Refreshing&hellip;
            </p>
          </div>
        </div>
      )}

      {/* ── Error ───────────────────────────────────────────────────── */}
      {error && (
        <div
          className="flex items-start gap-3 px-4 py-3.5 rounded-xl"
          style={{ background: "rgba(239,68,68,0.08)", border: "1px solid rgba(239,68,68,0.25)" }}
        >
          <AlertCircle size={16} className="text-red-400 mt-0.5 shrink-0" />
          <div>
            <p className="text-sm font-semibold text-red-400">Error</p>
            <p className="text-xs mt-0.5" style={{ color: "rgba(255,255,255,0.45)" }}>{error}</p>
          </div>
        </div>
      )}

      {/* ── Spacer ──────────────────────────────────────────────────── */}
      <div className="flex-1" />

      {/* ── Actions ─────────────────────────────────────────────────── */}
      <div className="flex items-center justify-end gap-2 pt-2" style={{ borderTop: "1px solid rgba(255,255,255,0.07)" }}>
        <button
          onClick={reset}
          disabled={isBusy || phase === "idle"}
          className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-medium transition-all disabled:opacity-30 disabled:cursor-not-allowed"
          style={{
            background: "rgba(255,255,255,0.05)",
            border: "1px solid rgba(255,255,255,0.1)",
            color: "rgba(255,255,255,0.55)",
          }}
        >
          <RotateCcw size={12} />
          Clear
        </button>
        <button
          onClick={submitData}
          disabled={isBusy || data.length === 0 || isDone}
          className="flex items-center gap-2 px-5 py-2 rounded-lg text-xs font-semibold transition-all disabled:opacity-30 disabled:cursor-not-allowed"
          style={{
            background: isBusy || data.length === 0 || isDone ? "rgba(34,197,94,0.3)" : "rgba(34,197,94,0.85)",
            border: "1px solid rgba(34,197,94,0.4)",
            color: "#fff",
          }}
        >
          {isBusy && <SpinnerIcon size={12} />}
          {isParsing   ? "Reading file…"  :
           isGeocoding ? "Geocoding…"     :
           isUploading ? "Uploading…"     :
           isDone      ? "Done ✓"         : "Upload Dataset"}
        </button>
      </div>
    </div>
  );
};

/* ── Status card ──────────────────────────────────────────────────────── */
function StatusCard({
  icon, label, sub, color, children,
}: {
  icon: React.ReactNode;
  label: string;
  sub: string;
  color: string;
  children?: React.ReactNode;
}) {
  return (
    <div
      className="px-4 py-3.5 rounded-xl flex flex-col gap-2.5"
      style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.08)" }}
    >
      <div className="flex items-center gap-2.5">
        {icon}
        <div className="flex-1 min-w-0">
          <p
            className="text-xs font-semibold"
            style={{ color }}
            dangerouslySetInnerHTML={{ __html: label }}
          />
          <p className="text-[10px] mt-0.5" style={{ color: "rgba(255,255,255,0.3)" }}>{sub}</p>
        </div>
      </div>
      {children}
    </div>
  );
}

/* ── Stat cell ────────────────────────────────────────────────────────── */
function StatCell({
  icon, label, value, sub, color, border,
}: {
  icon: React.ReactNode;
  label: string;
  value: number;
  sub: string;
  color: string;
  border: boolean;
}) {
  return (
    <div
      className="flex flex-col gap-1 px-4 py-3"
      style={border ? { borderRight: "1px solid rgba(255,255,255,0.07)" } : {}}
    >
      <div className="flex items-center gap-1.5">
        {icon}
        <span className={`text-[10px] font-semibold ${color}`}>{label}</span>
      </div>
      <span className={`text-xl font-bold tabular-nums leading-none ${color}`}>{value}</span>
      <span className="text-[9px] font-mono" style={{ color: "rgba(255,255,255,0.2)" }}>{sub}</span>
    </div>
  );
}

/* ── Spinner ──────────────────────────────────────────────────────────── */
function SpinnerIcon({ size = 14, color = "rgba(255,255,255,0.6)" }: { size?: number; color?: string }) {
  return (
    <svg
      width={size} height={size} viewBox="0 0 24 24"
      className="animate-spin shrink-0"
      style={{ color }}
    >
      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" fill="none" strokeOpacity="0.25" />
      <path d="M12 2a10 10 0 0 1 10 10" stroke="currentColor" strokeWidth="3" fill="none" strokeLinecap="round" />
    </svg>
  );
}

export default ExcelUploader;
